import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { FindItResult } from '../findItSearch';
import { findItMessages as m, servicesPanelMessages } from '../servicesPanelMessages';
import { FindItStatus } from './useFindItSearch';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for FindIt; each part uses its own slice of it */
import styles from './FindIt.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FindItResultsProps {
  serviceName: string;
  status: FindItStatus;
  results: FindItResult[];
  /** The address whose save runs now. */
  savingUrl: string | null;
  /** The address whose save failed, for the line under its button. */
  failedUrl: string | null;
  onPick: (result: FindItResult) => void;
  onRetry: () => void;
}

const SKELETON_ROWS = ['a', 'b', 'c'];

/** The body of "Find it" (design d4): the results, the loading rows, no match and the error. */
const FindItResultsComponent: React.FC<FindItResultsProps> = ({
  serviceName,
  status,
  results,
  savingUrl,
  failedUrl,
  onPick,
  onRetry,
}) => {
  const intl = useIntl();
  if (status === 'loading') {
    return (
      <ul className={styles.body} aria-busy="true">
        {SKELETON_ROWS.map((key) => (
          <li key={key} className={styles.result}>
            <span className={styles.cover} />
            <div>
              <span className={styles.bar} />
              <span className={clsx(styles.bar, styles.barShort)} />
            </div>
          </li>
        ))}
      </ul>
    );
  }
  if (status === 'failed' || results.length === 0) {
    const failed = status === 'failed';
    return (
      <div className={styles.empty} role="status">
        <b>{intl.formatMessage(failed ? m.failedTitle : m.noMatchTitle, { service: serviceName })}</b>
        <p>{intl.formatMessage(failed ? m.failedBody : m.noMatchBody)}</p>
        {failed && (
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onRetry}>
            {intl.formatMessage(m.tryAgain)}
          </button>
        )}
      </div>
    );
  }
  return (
    <ul className={styles.body}>
      {results.map((result, index) => {
        const isSaving = savingUrl === result.url;
        return (
          <li key={result.key} className={clsx(styles.result, isSaving && styles.resultPicked)}>
            {result.imageUrl === null ? (
              <span className={styles.cover} />
            ) : (
              <img className={styles.cover} src={result.imageUrl} alt="" />
            )}
            <div className={styles.resultText}>
              <b>{result.title}</b>
              <small>
                {[result.artistNames, result.albumName].filter((part) => part !== null && part !== '').join(' · ')}
              </small>
            </div>
            <button
              type="button"
              className={clsx('btn btn-sm', index === 0 ? 'btn-primary' : 'btn-outline-secondary')}
              disabled={savingUrl !== null}
              onClick={() => onPick(result)}
            >
              {intl.formatMessage(isSaving ? servicesPanelMessages.saving : m.useThis)}
            </button>
            {failedUrl === result.url && (
              <span className={styles.saveError}>{intl.formatMessage(servicesPanelMessages.saveFailed)}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export const FindItResults = TypedMemo(FindItResultsComponent);
